/**
 * MOCK/TEMPORARY — the songs table, standing in for the one the server will own.
 *
 * ============================================================================
 * WHY THIS FILE HAD TO EXIST, and it is the whole of Batch 7 §1.6 in one sentence:
 * `discoveryApi.getShowcase(songs, …)` and `shareApi.getShared(slug, songs)` took the
 * caller's library as an argument, which made THE BROWSER THE AUTHORITATIVE SOURCE OF PUBLIC
 * VISIBILITY. Whatever a client passed in was the public catalogue, and whatever it withheld
 * was not — so "is this song public" was answered by the same process that would like the
 * answer to be yes.
 *
 * Removing the parameter is only half a fix, because something still has to hold the
 * catalogue. A read cannot answer "what has everyone published" out of nothing. So the mock
 * owns a table, the way a server owns a table, and the two public reads query it:
 *
 *   discoveryApi.getShowcase(options)   → GET /discovery/showcase
 *   shareApi.getShared(slug)            → GET /share/:slug
 *
 * Neither one can now be handed a library, which is the property worth having: a component
 * that wants a song in the catalogue has to PUBLISH it through the write seam, and publishing
 * is an action on a record rather than an argument to a query.
 *
 * WHAT THIS IS NOT. It is not a security boundary and it is not persistence. It is
 * module-level memory in the same process as the UI, so it is editable from devtools and it
 * is gone on reload — every caveat in api/README.md and in the modules around it still
 * applies, unchanged. What it fixes is narrower and structural: the SHAPE of the two public
 * reads is now the shape a real endpoint has, so replacing them is a body swap rather than a
 * signature change that ripples into every caller. The `client-side-visibility-filter`
 * blocker in discoveryApi is still raised, because this table is still in the browser.
 *
 * DELETED WHOLE when the backend lands. Nothing outside src/api/ may import it except
 * songsStore's seed — see `mockSeedLibrary` for why that one exception exists and what
 * replaces it.
 * ============================================================================
 */
import { createInitialSongs } from '../data/songs';
import type { Song } from '../types/domain';

/**
 * The one seed library in the process.
 *
 * ============================================================================
 * ONE CALL TO `createInitialSongs()`, AND THE SINGULARITY IS THE POINT. The factory mints
 * fresh `newId()` values on every call (deliberately — see data/songs.ts), so two calls
 * produce two libraries describing the same four songs under DIFFERENT IDS.
 *
 * That was invisible while nothing crossed between them: songsStore seeded itself from one
 * call and `songsApi.getSongs()` answered from another, and since discovery was handed the
 * store's copy, the mock's copy was never consulted. It stops being invisible the moment a
 * read queries this table instead — publishing a song in the store would patch an id the
 * table has never heard of, and the Home page would stay empty while the Library said the
 * track was discoverable.
 *
 * So the seed is created once, here, and both the store's initial state and this table come
 * from it. Frozen only by convention; `reset()` re-copies it rather than re-creating it.
 * ============================================================================
 */
const SEED: readonly Song[] = createInitialSongs();

/** A detached copy, so a caller mutating what it got cannot reach into the table. */
function copy(song: Song): Song {
  return { ...song, versions: song.versions?.map((v) => ({ ...v })) };
}

/** The table itself. Newest-first ordering is the reader's business, not this file's. */
let table: Song[] = SEED.map(copy);

/**
 * The seed library, for `songsStore`'s initial state.
 *
 * ============================================================================
 * THE ONE EXPORT THAT LEAVES src/api/, AND WHAT REPLACES IT. songsStore is seeded
 * synchronously at module scope, which is a mock-shaped design: with a backend the store
 * starts EMPTY and `loadSongs()` fills it from `GET /songs` behind the read states that now
 * exist for exactly that. Until then the seed has to come from somewhere, and it has to be
 * the same four ids this table holds — see `SEED` for what goes wrong otherwise.
 *
 * So this is deliberately not `songsApi.getSongs()`: that one is async (api/README rule 1)
 * and cannot answer a module-level initialiser. It is the mock's seed, named as the mock's
 * seed, and the day the store's initial state becomes `[]` this export loses its last
 * caller.
 * ============================================================================
 */
export function mockSeedLibrary(): Song[] {
  return SEED.map(copy);
}

/**
 * Every song the table holds — the account's library AND everyone else's.
 *
 * There is only one account, so today those are the same set. The distinction is real
 * anyway, and it is why `getShowcase` filters rather than returning this: a public read
 * must apply `status === 'ready' && visibility === 'public'` itself, because a private song
 * that reaches a response has leaked whatever the caller does with it afterwards.
 */
export function mockCatalogueSongs(): Song[] {
  return table.map(copy);
}

/** One song by id, or null. Detached, like every other read here. */
export function mockCatalogueSong(id: string): Song | null {
  const found = table.find((s) => s.id === id);
  return found ? copy(found) : null;
}

/**
 * Records a song, replacing any existing row with the same id.
 *
 * Called by `generationApi.createGeneration`, which is where a song comes into existence.
 * A generated song arrives `visibility: 'private'` and therefore lands in the table without
 * entering the catalogue — finishing is not publishing, and the table stores the record
 * rather than deciding who may see it.
 */
export function mockCatalogueUpsert(song: Song): void {
  const next = copy(song);
  const at = table.findIndex((s) => s.id === song.id);
  if (at === -1) table = [next, ...table];
  else table = table.map((s) => (s.id === song.id ? next : s));
}

/**
 * Applies a partial edit to one row. Unknown ids are ignored.
 *
 * IGNORED RATHER THAN THROWN, because the mock is not the place to invent a 404 for a
 * fire-and-forget write. `songsStore` patches optimistically and does not await these calls
 * (see its own comments), so a rejection here would surface as an unhandled rejection in a
 * screen that has already moved on. A real endpoint answers `404`, and the store will have to
 * handle it when these writes become awaited.
 */
export function mockCataloguePatch(id: string, patch: Partial<Song>): void {
  table = table.map((s) => (s.id === id ? copy({ ...s, ...patch }) : s));
}

/** Removes one row. Unknown ids are ignored, for the reason `mockCataloguePatch` gives. */
export function mockCatalogueRemove(id: string): void {
  table = table.filter((s) => s.id !== id);
}

/**
 * Replaces the whole table. TESTS ONLY.
 *
 * The table is module state, so a test that publishes something leaks it into the next test
 * in the file — the same hazard the Zustand snapshots in src/test/setup.ts exist for, which
 * is why `reset()` below is called from the same place. This is the other half: a test that
 * needs a specific catalogue sets one, rather than reaching for a store and hoping the read
 * still consults it.
 */
export function mockCatalogueSetLibrary(songs: readonly Song[]): void {
  table = songs.map(copy);
}

/** Restores the seed. Called before every test; see `SEED` on why it re-copies. */
export function mockCatalogueReset(): void {
  table = SEED.map(copy);
}
